import { Link } from "react-router-dom";
import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Scale, ClipboardCheck, HardHat, Atom, FileCheck, Send, Shield, Cloud, Lock, Eye, Gavel, Store } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { z } from "zod";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import FAQ from "@/components/FAQ";
import { faqPage, orgNode, webSite } from "@/lib/seo";
import StateActivityMap from "@/components/StateActivityMap";
import CursorSpotlight from "@/components/CursorSpotlight";
import { useCountUp } from "@/hooks/use-count-up";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import southernCompanyLogo from "@/assets/southern-company-logo.webp";
import xcelEnergyLogo from "@/assets/xcel-energy-logo.webp";

import pgeLogo from "@/assets/pge-logo.webp";
import entergyLogo from "@/assets/entergy-logo.svg";
import dukeEnergyLogo from "@/assets/duke-energy-logo.webp";
import sceLogo from "@/assets/sce-logo.webp";
import heroDrone from "@/assets/hero-drone.mp4.asset.json";
import soc2Badge from "@/assets/soc2-badge.webp";

const CALENDLY_URL = "https://calendly.com/cjudd-acciongroup/30min";

const services = [
  { icon: Scale, title: "Competitive procurement", desc: "Tech platform for end-to-end management of energy solicitations." },
  { icon: ClipboardCheck, title: "Independent evaluation & monitoring", desc: "Objective oversight that supports regulatory review." },
  { icon: HardHat, title: "Construction monitoring & auditing", desc: "Verification and reporting through project execution and commercial operation." },
  { icon: Atom, title: "Nuclear decommissioning advisory", desc: "Specialized guidance for complex retirement processes." },
  { icon: Gavel, title: "Regulatory proceeding support", desc: "Advisory and technology solutions for Rate Cases, IRPs and other regulatory processes." },
  { icon: Store, title: "Marketplace design & tech", desc: "Innovative program design and technology to help utilities, large load customers and developers meet unprecedented load growth." },
];

const oversightPoints = [
  "Equal access to information for all bidders",
  "Consistent, transparent communication",
  "Clear, well managed separation protocols",
  "Model lockdown procedure to certify evaluation process integrity",
  "A complete audit trail for regulators and stakeholders",
];

const differentiators = [
  { title: "Built for energy procurement, not adapted to it", desc: "Our proprietary platform runs your entire solicitation in one place. From RFP launch to final evaluation, everything is structured, secure, and fully auditable." },
  { title: "Fair by design", desc: "Every bidder gets access to the same information at the same time to meet FERC's Allegheny Standard. Separation protocols between evaluators and company owned proposal teams are enforced automatically. Every bidder action is tracked, time-stamped, and preserved." },
  { title: "Independent when it matters most", desc: "We ensure your process is unbiased, defensible, and aligned with regulatory expectations from day one. We document every aspect of the procurement to accelerate objective regulatory review and portfolio approval." },
];

const stats = [
  { value: "240+", label: "Energy procurements completed" },
  { value: "28", label: "Experience across states and territories" },
  { value: "25+", label: "Years of experience" },
  { value: "12.5GW", label: "Power procured through Accion-administered RFPs" },
];

const outcomes = [
  "A procurement process you can defend",
  "Strong alignment with regulatory expectations",
  "A level playing field for bidders",
  "Clear, structured decision-making from start to finish",
];

const audiences = [
  {
    label: "Electric utilities",
    desc: "Investor-owned, municipal, and cooperative utilities running competitive solicitations for generation, storage, and capacity.",
  },
  {
    label: "Regulatory agencies & commissions",
    desc: "Public utility commissions and oversight bodies requiring independent evaluation of utility procurement processes.",
  },
  {
    label: "Energy program administrators",
    desc: "State agencies and program administrators managing renewable, efficiency, and clean energy procurement initiatives.",
  },
];

const emailSchema = z
  .string()
  .trim()
  .email({ message: "Please enter a valid email address" })
  .max(255, { message: "Email must be less than 255 characters" });

const CountStat = ({ value }: { value: string }) => {
  const { ref, display } = useCountUp(value);
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="font-display text-4xl lg:text-5xl font-bold mb-2 transition-transform duration-300 group-hover:-translate-y-0.5 tabular-nums"
    >
      {display}
    </div>
  );
};

const Index = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const reducedMotion = useReducedMotion();

  const handleDeveloperSignup = (e: React.FormEvent) => {
    e.preventDefault();
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      toast({
        title: "Invalid email",
        description: result.error.issues[0].message,
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    // Frontend-only for now — show confirmation
    setTimeout(() => {
      toast({
        title: "You're on the list",
        description: "We'll notify you when new RFP opportunities are posted on PowerPro.",
      });
      setEmail("");
      setSubmitting(false);
    }, 400);
  };

  const homeFaqs = [
    {
      q: "What does Accion Group do?",
      a: "Accion Group helps electric utilities and regulators design, run, and oversee competitive energy procurements. We provide independent evaluation and monitoring services and operate PowerPro, a SOC 2 Type II certified RFP platform purpose-built for regulated energy solicitations.",
    },
    {
      q: "What is PowerPro?",
      a: "PowerPro is Accion's cloud-hosted, SOC 2 Type II certified RFP management platform built specifically for regulated energy procurements. It manages the full solicitation lifecycle, from RFP launch through bid evaluation, with structured forms, secure document exchange, and a complete regulator-ready audit trail.",
    },
    {
      q: "Who is PowerPro for?",
      a: "PowerPro is built for investor-owned, municipal, and cooperative electric utilities running competitive solicitations for generation, storage, and capacity, as well as for regulatory commissions and program administrators that require independent oversight of utility procurement processes.",
    },
    {
      q: "How do I book a demo?",
      a: "You can book a demo with the Accion team directly through our Calendly link from any 'Book a Demo' button on the site, or by visiting our Contact page.",
    },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="PowerPro RFP Platform for Electric Utilities | Accion Group"
        description="Accion Group helps utilities and regulators run secure, transparent energy procurements with PowerPro, a SOC 2 Type II certified RFP platform. Book a demo."
        canonical="/"
        jsonLd={[webSite(), orgNode, faqPage(homeFaqs)] as never}
      />
      <Navbar />
      <main id="main-content" tabIndex={-1} className="flex-1 outline-none">
        {/* Hero */}
        <section className="relative overflow-hidden bg-primary text-primary-foreground">
          {/* Drone footage background */}
          <video
            src={heroDrone.url}
            autoPlay={!reducedMotion}
            muted
            loop
            playsInline
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover opacity-25 pointer-events-none"
          />
          {/* Color wash + fade-out mask so video is subtle and blends into the page */}
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-b from-primary/70 via-primary/60 to-primary"
          />
          {/* Animated drifting gradient glow */}
          <div className="absolute inset-0 opacity-[0.18] pointer-events-none" aria-hidden="true">
            <div className="absolute -top-24 -right-24 h-[28rem] w-[28rem] rounded-full bg-accent blur-3xl animate-glow-drift" />
            <div className="absolute bottom-0 -left-24 h-[28rem] w-[28rem] rounded-full bg-white blur-3xl animate-glow-drift [animation-delay:-6s]" />
          </div>
          {/* Subtle grid pattern overlay */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(to right, hsl(0 0% 100% / 0.6) 1px, transparent 1px), linear-gradient(to bottom, hsl(0 0% 100% / 0.6) 1px, transparent 1px)",
              backgroundSize: "56px 56px",
              maskImage: "radial-gradient(ellipse at center, black 35%, transparent 75%)",
            }}
          />

          <CursorSpotlight className="container mx-auto px-4 lg:px-8 py-24 lg:py-32 relative" radius={520}>
            <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl">
              <motion.p variants={fadeUp} className="text-accent-foreground/90 font-semibold text-sm uppercase tracking-widest mb-6 inline-block bg-accent px-3 py-1 rounded">
                The Future of Power Procurement
              </motion.p>
              <motion.h1 variants={fadeUp} className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Run faster energy procurements.
                <span className="block text-primary-foreground/70 mt-2">With clarity, fairness, and confidence.</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg lg:text-xl text-primary-foreground/80 max-w-2xl mb-4">
                Accion helps utilities and regulators design and run secure, efficient, and transparent energy procurements.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="shimmer group inline-flex items-center gap-2 bg-accent text-accent-foreground font-semibold px-6 py-3 rounded-md hover:bg-accent/90 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 ease-out"
                >
                  <span className="relative z-[2] inline-flex items-center gap-2">
                    Get in Touch
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-out group-hover:translate-x-1" />
                  </span>
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 border border-primary-foreground/30 text-primary-foreground font-semibold px-6 py-3 rounded-md hover:bg-primary-foreground/10 hover:-translate-y-0.5 transition-all duration-300 ease-out"
                >
                  Learn About Us
                </Link>
              </motion.div>
            </motion.div>
          </CursorSpotlight>
        </section>

        {/* What we do */}
        <section id="services" className="py-20 lg:py-28">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              variants={stagger}
              className="max-w-3xl mb-14"
            >
              <motion.p variants={fadeUp} className="text-accent font-semibold text-sm uppercase tracking-widest mb-4">What we do</motion.p>
              <motion.h2 variants={fadeUp} className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Accion supports utilities and regulators streamlining solicitations and regulatory proceedings
              </motion.h2>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              variants={stagger}
              className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
            >
              {services.map((s) => (
                <motion.div
                  key={s.title}
                  variants={fadeUp}
                  className="gradient-border group relative p-6 rounded-lg bg-card hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ease-out overflow-hidden"
                >
                  <span className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-accent scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300 z-[2]" />
                  <div className="h-12 w-12 rounded-xl bg-primary/5 text-primary flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-accent group-hover:text-accent-foreground group-hover:scale-110 group-hover:rotate-3 relative z-[2]">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display font-semibold text-lg text-foreground mb-2 transition-colors duration-300 group-hover:text-accent relative z-[2]">{s.title}</h3>
                  <p className="text-sm text-muted-foreground relative z-[2]">{s.desc}</p>
                </motion.div>
              ))}
            </motion.div>
            <p className="text-base text-muted-foreground max-w-3xl mt-12">
              For more than 25 years, we've worked alongside utilities and regulators across the U.S. to ensure procurement processes are fair, rigorous, and built to hold up under scrutiny.
            </p>
          </div>
        </section>

        {/* Who we work with */}
        <section id="audiences" className="py-24 lg:py-32 bg-secondary/40 border-y border-border">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-12 gap-6 lg:gap-12">
              {/* Sticky heading column */}
              <div className="col-span-12 lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
                <p className="text-accent font-semibold text-xs uppercase tracking-[0.2em] mb-6">
                  Who we work with
                </p>
                <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground leading-[1.05] tracking-tight">
                  Accion works with organizations shaping the future of energy.
                </h2>
              </div>

              {/* Audience entries */}
              <div className="col-span-12 lg:col-span-8 lg:col-start-5">
                {audiences.map((a, i) => (
                  <div
                    key={a.label}
                    className="group relative grid grid-cols-12 gap-4 py-10 lg:py-12 border-b border-border last:border-b-0 first:pt-0"
                  >
                    <div className="col-span-12 sm:col-span-4 flex items-start gap-4">
                      <span className="font-display text-5xl lg:text-6xl font-bold text-accent leading-none tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="col-span-12 sm:col-span-8">
                      <h3 className="font-display text-2xl lg:text-[1.75rem] font-bold text-foreground leading-tight mb-3">
                        {a.label}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {a.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Trusted by */}
        <section className="py-16 border-b border-border/50 bg-secondary/40">
          <div className="container mx-auto px-4 lg:px-8 text-center mb-8">
            <p className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">
              Trusted by Leading Utilities from Coast to Coast
            </p>
          </div>
          <div className="relative overflow-hidden w-full">
            <div className="flex w-max animate-marquee items-center">
              {[...Array(2)].map((_, dup) => (
                <div key={dup} className="flex items-center gap-12 lg:gap-20 shrink-0 pr-12 lg:pr-20">
                  <img loading="lazy" decoding="async" src={southernCompanyLogo} alt="Southern Company" className="h-8 object-contain" />
                  <img loading="lazy" decoding="async" src={xcelEnergyLogo} alt="Xcel Energy" className="h-10 object-contain" />
                  
                  <img loading="lazy" decoding="async" src={pgeLogo} alt="Pacific Gas and Electric" className="h-20 object-contain" />
                  <img loading="lazy" decoding="async" src={entergyLogo} alt="Entergy" className="h-10 object-contain" />
                  <img loading="lazy" decoding="async" src={dukeEnergyLogo} alt="Duke Energy" className="h-16 object-contain" />
                  <img loading="lazy" decoding="async" src={sceLogo} alt="Southern California Edison" className="h-10 object-contain" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Independent oversight */}
        <section className="py-20 lg:py-28 bg-secondary/40 border-y border-border">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-start">
              <div>
                <p className="text-accent font-semibold text-sm uppercase tracking-widest mb-4">
                  Independent Process Oversight
                </p>
                <h2 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-6">
                  Foundational to Accion's work is serving as an Independent Evaluator and Independent Monitor.
                </h2>
                <p className="text-muted-foreground text-lg">
                  When independence matters, we're there to design in fairness from day one so the results stand up to review.
                </p>
              </div>
              <div className="bg-card border border-border rounded-lg p-8">
                <h3 className="font-display font-semibold text-foreground mb-6">We oversee procurement processes to ensure:</h3>
                <ul className="space-y-2">
                  {oversightPoints.map((point) => (
                    <li
                      key={point}
                      className="group flex items-start gap-3 rounded-md p-2 -mx-2 transition-colors duration-300 hover:bg-secondary/60"
                    >
                      <FileCheck className="h-5 w-5 text-accent shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110" />
                      <span className="text-foreground transition-colors duration-300 group-hover:text-accent">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Differentiators */}
        <section id="differentiators" className="py-20 lg:py-28">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mb-14">
              <p className="text-accent font-semibold text-sm uppercase tracking-widest mb-4">THE ACCION DIFFERENCE</p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-foreground">
                Purpose-built. Fair by design. Always Independent.
              </h2>
            </div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              variants={stagger}
              className="grid gap-8 md:grid-cols-3"
            >
              {differentiators.map((d, i) => (
                <motion.div
                  key={d.title}
                  variants={fadeUp}
                  className="group relative rounded-lg p-6 -m-6 transition-all duration-300 hover:bg-secondary/40"
                >
                  <div className="text-5xl font-display font-bold text-accent/20 mb-3 transition-all duration-300 group-hover:text-accent/60 group-hover:-translate-y-0.5">0{i + 1}</div>
                  <h3 className="font-display font-semibold text-xl text-foreground mb-3 transition-colors duration-300 group-hover:text-accent">{d.title}</h3>
                  <p className="text-muted-foreground">{d.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Proven at scale */}
        <section id="experience" className="relative overflow-hidden py-20 lg:py-24 bg-primary text-primary-foreground scroll-mt-20">
          <CursorSpotlight className="container mx-auto px-4 lg:px-8" radius={460}>
            <div className="max-w-2xl mb-12">
              <p className="text-primary-foreground font-semibold text-sm uppercase tracking-widest mb-4">Proven at scale</p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold">
                Trusted by utilities and regulators nationwide.
              </h2>
            </div>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label} className="group border-l-2 border-accent pl-5 transition-all duration-300 hover:border-l-4 hover:pl-6">
                  <CountStat value={s.value} />
                  <div className="text-sm text-primary-foreground/70">{s.label}</div>
                </div>
              ))}
            </div>
          </CursorSpotlight>
        </section>

        {/* State activity map */}
        <section className="py-20 lg:py-24 bg-secondary/40 border-b border-border">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <p className="text-accent font-semibold text-sm uppercase tracking-widest mb-4">Where we work</p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-foreground">
                Geographic scope Accion's energy procurement experience.
              </h2>
            </div>
            <StateActivityMap />
          </div>
        </section>


        {/* Data Security */}
        <section id="data-security" className="py-20 lg:py-24 bg-background border-b border-border scroll-mt-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid gap-12 lg:gap-16 lg:grid-cols-2 items-center max-w-6xl mx-auto">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={viewportOnce}
                variants={stagger}
              >
                <motion.p
                  variants={fadeUp}
                  className="text-accent font-semibold text-sm uppercase tracking-widest mb-4"
                >
                  Security & Compliance
                </motion.p>
                <motion.h2
                  variants={fadeUp}
                  className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-5 leading-tight"
                >
                  Enterprise-Grade Protection for Sensitive Procurement Data
                </motion.h2>
                <motion.p
                  variants={fadeUp}
                  className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-xl"
                >
                  Accion's products are SOC 2 compliant with granular, role-based
                  access controls ensuring users can only access materials they're
                  authorized for. All data is encrypted in transit and at rest.
                </motion.p>
                <motion.div variants={fadeUp} className="flex items-center gap-5 flex-wrap">
                  <Link
                    to="/security"
                    className="group relative inline-flex items-center gap-2 rounded-md border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground overflow-hidden transition-colors hover:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-accent translate-x-[-101%] group-hover:translate-x-0 transition-transform duration-500 ease-out"
                    />
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 rounded-md ring-2 ring-accent/40 animate-pulse pointer-events-none"
                    />
                    <span className="relative">Learn More</span>
                    <ArrowRight className="relative h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <img
                    src={soc2Badge}
                    alt="AICPA SOC 2 for Service Organizations badge"
                    className="h-28 w-28 shrink-0"
                    loading="lazy"
                  />
                </motion.div>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={viewportOnce}
                variants={stagger}
                className="grid grid-cols-1 sm:grid-cols-2 gap-5"
              >
                {[
                  { icon: Shield, title: "SOC 2 Type II", text: "Independently audited and certified to the highest security standards." },
                  { icon: Cloud, title: "U.S.-Based Cloud Hosting", text: "Hosted exclusively in U.S.-based data centers with continuous monitoring." },
                  { icon: Lock, title: "Post-Quantum Encryption", text: "Post-quantum cryptography (PQC) protects all data in transit and at rest." },
                  { icon: Eye, title: "Zero-Trust Audit Logs", text: "Zero-Trust controls track every access, upload, download, and modification for regulatory review." },
                ].map(({ icon: Icon, title, text }) => (
                  <motion.div
                    key={title}
                    variants={fadeUp}
                    className="rounded-2xl bg-primary text-primary-foreground p-6 lg:p-7 shadow-sm hover:shadow-lg transition-shadow"
                  >
                    <Icon className="h-7 w-7 text-primary-foreground/90 mb-5" strokeWidth={1.5} />
                    <h3 className="font-display text-lg font-bold mb-2">{title}</h3>
                    <p className="text-sm text-primary-foreground/70 leading-relaxed">{text}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>


        {/* FAQ */}
        <FAQ items={homeFaqs} />

        {/* Final CTA + Developer signup, side by side */}
        <section className="py-20 lg:py-28">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-2 lg:gap-10 max-w-6xl mx-auto items-stretch">
              {/* Final CTA */}
              <div className="bg-primary text-primary-foreground rounded-2xl p-8 lg:p-12 relative overflow-hidden flex flex-col">
                <div className="absolute inset-0 opacity-10" aria-hidden="true">
                  <div className="absolute top-0 right-0 h-64 w-64 rounded-full bg-accent blur-3xl" />
                </div>
                <div className="relative flex flex-col h-full">
                  <p className="text-white font-semibold text-sm uppercase tracking-widest mb-4">For utilities & regulators</p>
                  <h2 className="font-display text-2xl lg:text-3xl font-bold mb-4 leading-tight">
                    Streamline and accelerate your next energy solicitation.
                  </h2>
                  <p className="text-base text-primary-foreground/80 mb-8">
                    Run a faster process that's secure, transparent, and built to hold up under scrutiny.
                  </p>
                  <div>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-semibold px-6 py-3 rounded-md hover:bg-accent/90 transition-colors"
                    >
                      Start the conversation
                      <ArrowRight className="h-5 w-5" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Developer RFP signup */}
              <div className="bg-card border border-border rounded-2xl p-8 lg:p-12 flex flex-col shadow-sm">
                <p className="text-accent font-semibold text-sm uppercase tracking-widest mb-4">For developers & bidders</p>
                <h2 className="font-display text-2xl lg:text-3xl font-bold text-foreground mb-4 leading-tight">
                  Stay ahead of upcoming RFP opportunities.
                </h2>
                <p className="text-base text-muted-foreground mb-8">
                  Sign up to be notified when new solicitations are posted on PowerPro.
                </p>
                <form onSubmit={handleDeveloperSignup} className="space-y-3">
                  <label htmlFor="developer-email" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="developer-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your work email address"
                    maxLength={255}
                    className="w-full h-12 rounded-md border border-border bg-background px-4 text-base text-foreground placeholder:text-muted-foreground transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:border-accent focus-visible:shadow-[0_0_0_4px_hsl(var(--accent)/0.15)]"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="shimmer w-full h-12 inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground font-semibold rounded-md hover:bg-accent/90 transition-colors disabled:opacity-60"
                  >
                    <span className="relative z-[2] inline-flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      {submitting ? "Signing up..." : "Notify Me"}
                    </span>
                  </button>
                  <p className="text-xs text-muted-foreground text-center">
                    We respect your privacy. Unsubscribe at any time.
                  </p>
                </form>
                <div className="my-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <a
                  href="https://pro.accionpower.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-12 inline-flex items-center justify-center gap-2 border border-primary text-primary font-semibold rounded-md hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  Sign up / Log in to PowerPro
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
